import { useCallback, useMemo } from 'react';

import { parseSort } from './url-state';

export type SortDir = 'asc' | 'desc';

/** Generic column sort over a getter map; values compare as numbers when both parse. */
export function sortRows<T>(
  rows: T[],
  column: string | null,
  dir: SortDir,
  getters: Record<string, (row: T) => string | number | null | undefined>,
): T[] {
  if (!column) return rows;
  const get = getters[column];
  if (!get) return rows;
  const mul = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const an = typeof av === 'number' ? av : Number(av);
    const bn = typeof bv === 'number' ? bv : Number(bv);
    if (
      !Number.isNaN(an) &&
      !Number.isNaN(bn) &&
      String(av).trim() !== '' &&
      String(bv).trim() !== ''
    ) {
      return (an - bn) * mul;
    }
    return (
      String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * mul
    );
  });
}

/** Sort state for a table, read from and written to a ?sort=col:dir URL value. */
export function useTableSort(sortValue: string, setSort: (v: string) => void) {
  const parsed = useMemo(() => parseSort(sortValue), [sortValue]);
  const toggle = useCallback(
    (column: string) => {
      if (parsed?.column === column) {
        setSort(parsed.dir === 'asc' ? `${column}:desc` : '');
      } else {
        setSort(`${column}:asc`);
      }
    },
    [parsed, setSort],
  );
  return { column: parsed?.column ?? null, dir: parsed?.dir ?? 'asc', toggle } as const;
}
